import { useMemo } from 'react';
import type { GetAgendaRecordsRes, JoinedFilteredAgendaRes, AgendaRecordItem } from 'types';

export interface UseFlattenFilteredRecordsProps {
  filteredData: GetAgendaRecordsRes<JoinedFilteredAgendaRes[]>;
}

/**
 * Flattens the joined filtered records response into a simple list of AgendaRecordItems.
 *
 * The API returns records with mainRecord, nestedRecord, and secondLevelNestedRecord.
 * This hook extracts just the mainRecord from each item to maintain compatibility
 * with the existing table structure.
 */
export const useFlattenFilteredRecords = ({ filteredData }: UseFlattenFilteredRecordsProps): GetAgendaRecordsRes<AgendaRecordItem[]> => {
  return useMemo(() => {
    if (!filteredData?.items) {
      return {
        items: [],
        page: filteredData?.page ?? 1,
        pageSize: filteredData?.pageSize ?? 10,
        totalCount: filteredData?.totalCount ?? 0,
        hasPreviousPage: filteredData?.hasPreviousPage ?? false,
        hasNextPage: filteredData?.hasNextPage ?? false,
      };
    }

    return {
      items: filteredData.items.map((joinedRecord) => ({
        ...joinedRecord.mainRecord,
        attributes: [
          ...joinedRecord.mainRecord.attributes,
          ...(joinedRecord.nestedRecord?.attributes ?? []),
          ...(joinedRecord.secondLevelNestedRecord?.attributes ?? []),
        ],
      })),
      page: filteredData.page,
      pageSize: filteredData.pageSize,
      totalCount: filteredData.totalCount,
      hasPreviousPage: filteredData.hasPreviousPage,
      hasNextPage: filteredData.hasNextPage,
    };
  }, [filteredData]);
};
